import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, LibraryBig } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";

import { trackEvent } from "@/lib/tracking";
import {
  authorRef,
  organizationNode,
  personNode,
  publisherRef,
} from "@/lib/entity-jsonld";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/hooks/useReveal";

type Product = {
  /** Stable URL-safe slug. Locked here (not derived) so anchors and JSON-LD @id
      never drift if titles are edited for copy reasons. */
  slug: string;
  title: string;
  subtitle: string;
  desc: string;
  /** Destination for the buy CTA (Stan product page, storefront, or internal). */
  href: string;
  /** Numeric USD price, omitted for free/lead-magnet items. */
  price?: number;
  /** What the CTA actually opens — drives analytics + button copy. */
  destination: "stan_product" | "stan_store" | "internal";
  /** schema.org type for this information product. Defaults to "Book".
   *  Use "Course" for certifications/trainings and "Service" for live 1:1 work —
   *  mistyping a coaching call as a Book breaks rich-result eligibility. */
  schemaType?: "Book" | "CreativeWork" | "Course" | "Service";
};


/** Stan storefront root — verified live (200). */
const STORE_URL = "https://stan.store/DrSullySpeaks";
/** Per-product deep links, each captured from the live storefront CTA and
 *  verified to render the real product page with its price (see link audit).
 *  Format: stan.store/DrSullySpeaks/p/<stan-slug> — the Stan slug is NOT our
 *  slug, so never derive it; keep the mapping explicit. */
const stanProduct = (stanSlug: string) => `${STORE_URL}/p/${stanSlug}`;

const PRODUCTS: Product[] = [
  {
    slug: "sheep-bite",
    title: "SHEEP BITE!",
    subtitle: "Church Hurt Healing Workbook",
    desc: "A guided workbook to name, process, and heal from wounds inflicted inside the church.",
    href: stanProduct("sheep-bite--surviving-church-hurt-ebook"),
    price: 9.99,
    destination: "stan_product",
  },
  {
    slug: "built-on-purpose",
    title: "Built On Purpose",
    subtitle: "12-Week Purpose Clarity Guide",
    desc: "A three-month roadmap to clarify calling, align your work, and build a life on purpose.",
    href: stanProduct("built-on-purpose-ebook"),
    price: 9.99,
    destination: "stan_product",
  },
  {
    slug: "ai-is-not-your-enemy",
    title: "AI Is Not Your Enemy",
    subtitle: "Faith Leaders' AI Playbook",
    desc: "Practical, biblically-grounded direction for pastors and ministry leaders adopting AI with integrity.",
    href: stanProduct("ai-is-not-your-enemy-ebook"),
    price: 9.99,
    destination: "stan_product",
  },
  {
    slug: "honest-in-the-dark",
    title: "Honest In The Dark",
    subtitle: "Men's Prayer & Discipline Manual",
    desc: "A no-flinch manual for men who want a real prayer life and the discipline to sustain it.",
    href: stanProduct("honest-in-the-dark-ebook"),
    price: 9.99,
    destination: "stan_product",
  },
  {
    slug: "cheat-code",
    title: "Cheat Code",
    subtitle: "7-Day Challenge Journal (Digital PDF)",
    desc: "A week-long guided journal to break the cycle and rebuild your daily standard as a man.",
    href: stanProduct("the-cheat-code-for-men"),
    price: 14.99,
    destination: "stan_product",
  },
  {
    slug: "makechange-certification",
    title: "M.A.K.E.C.H.A.N.G.E. Certification (CERC)",
    subtitle: "16-Module Leadership Framework",
    desc: "The full certification training in the MAKECHANGE leadership framework — 16 modules, one system.",
    href: stanProduct("cohorts-and-certification"),
    price: 997,
    destination: "stan_product",
    schemaType: "Course",
  },
  {
    slug: "make-change-moment",
    title: "The MAKE CHANGE Moment™",
    subtitle: "Breakthrough Coaching Session",
    desc: "A single high-impact 1:1 session with Dr. Sully to unlock your next move.",
    href: stanProduct("dialindestiny-call-with-dr-sully"),
    price: 297,
    destination: "stan_product",
    schemaType: "Service",
  },
  {
    slug: "leadership-reset",
    title: "Leadership Reset",
    subtitle: "Burnout Recovery 90-Day Intensive",
    desc: "A 90-day recovery intensive for leaders running on fumes — restore rhythm, health, and vision.",
    // Delivered as a free guide opt-in on the storefront — no standalone product page.
    href: STORE_URL,
    destination: "stan_store",
    schemaType: "CreativeWork",
  },
  {
    slug: "spin-cycle",
    title: "SPIN CYCLE",
    subtitle: "7-Day Devotional (FREE Lead Magnet)",
    desc: "A free 7-day devotional to break the spin and reset your mind on what actually matters.",
    href: "/free",
    destination: "internal",
  },
];

/** JSON-LD requires absolute URLs — internal destinations get the site origin. */
const absoluteUrl = (href: string) =>
  href.startsWith("http") ? href : `https://drsullyspeaks.com${href}`;

/** Every product node points at the sitewide Organization / Person @ids so
 *  search engines attribute the whole catalog to one brand + one author. */
const PUBLISHER = organizationNode();
const AUTHOR = authorRef;

/**
 * Build the schema.org node for a single information product.
 * Books get Book-specific properties (bookFormat, ReadAction); courses get
 * Course properties; live 1:1 work is a Service. Everything else falls back to
 * CreativeWork, which is always valid for a digital resource.
 */
const productNode = (p: Product) => {
  const url = absoluteUrl(p.href);
  const type = p.schemaType ?? "Book";
  const isFree = p.price === undefined || /free/i.test(p.subtitle);

  const base: Record<string, unknown> = {
    "@type": type,
    "@id": `https://drsullyspeaks.com/ebooks#${p.slug}`,
    name: p.title,
    alternateName: p.subtitle,
    description: p.desc,
    url,
    inLanguage: "en",
    author: AUTHOR,
    creator: AUTHOR,
    publisher: publisherRef,
    provider: publisherRef,
    isAccessibleForFree: isFree,
    isPartOf: { "@id": "https://drsullyspeaks.com/ebooks#collection" },
    mainEntityOfPage: { "@id": "https://drsullyspeaks.com/ebooks#webpage" },
    offers: {
      "@type": "Offer",
      url,
      availability: "https://schema.org/InStock",
      priceCurrency: "USD",
      price: p.price ?? 0,
      seller: publisherRef,
    },
  };

  if (type === "Book") {
    base.bookFormat = "https://schema.org/EBook";
    base.workExample = {
      "@type": "Book",
      bookFormat: "https://schema.org/EBook",
      inLanguage: "en",
      potentialAction: { "@type": "ReadAction", target: url },
    };
  }

  if (type === "Course") {
    base.hasCourseInstance = {
      "@type": "CourseInstance",
      courseMode: "online",
      courseWorkload: "P16W",
    };
  }

  if (type === "Service") {
    base.serviceType = p.subtitle;
    base.areaServed = { "@type": "Country", name: "United States" };
  }

  return base;
};


const FAQS = [
  {
    q: "How do I get access to a resource after I buy it?",
    a: "Every purchase is delivered instantly through Stan Store. As soon as checkout completes you'll see a download link on the confirmation page and receive an email with the same link. You can return to that link any time — access does not expire.",
  },
  {
    q: "What file types are the books and workbooks?",
    a: "Books, guides, and playbooks are delivered as PDFs that read cleanly on phone, tablet, or desktop. Workbooks are fillable PDFs — you can type directly into them or print and write by hand.",
  },
  {
    q: "Who are these resources for?",
    a: "Faith leaders, church staff, ministry founders, and Christian professionals navigating purpose, church hurt, burnout, or building something on Kingdom foundations.",
  },
  {
    q: "Can I share or re-download a book later?",
    a: "Your download link stays active — bookmark the confirmation email so you can re-download on a new device. Resources are licensed for your personal use; please don't redistribute the files.",
  },
];

export default function Ebooks() {
  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <PageMeta
        title="eBooks & Digital Workbooks by Dr. Sully — Instant PDF Download"
        description="Instant-download eBooks, workbooks, and playbooks by Dr. Sully — church hurt healing, purpose clarity, faith-leader AI, men's discipline. Delivered as PDF."
        path="/ebooks"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            // Sitewide anchor — every page-level node links back to this via isPartOf.
            {
              "@type": "WebSite",
              "@id": "https://drsullyspeaks.com/#website",
              url: "https://drsullyspeaks.com",
              name: "Dr. Sully Speaks",
              publisher: publisherRef,
              inLanguage: "en",
            },
            PUBLISHER,
            personNode(),
            // Explicit WebPage node — gives search engines a canonical page entity
            // separate from the CollectionPage it specializes into, and lets the
            // FAQPage and ImageObject reference it via mainEntityOfPage / primaryImageOfPage.
            {
              "@type": "WebPage",
              "@id": "https://drsullyspeaks.com/ebooks#webpage",
              url: "https://drsullyspeaks.com/ebooks",
              name: "eBooks & Digital Workbooks by Dr. Sully",
              description:
                "Instant-download eBooks, workbooks, and playbooks by Dr. Sully — church hurt healing, purpose clarity, faith-leader AI, men's discipline.",
              inLanguage: "en",
              isPartOf: { "@id": "https://drsullyspeaks.com/#website" },
              about: publisherRef,
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: "https://drsullyspeaks.com/og/ebooks.jpg",
                width: 1200,
                height: 630,
              },
              breadcrumb: { "@id": "https://drsullyspeaks.com/ebooks#breadcrumbs" },
              mainEntity: { "@id": "https://drsullyspeaks.com/ebooks#collection" },
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: ["h1", "h2", "h3"],
              },
              datePublished: "2024-01-01",
              dateModified: new Date().toISOString().slice(0, 10),
            },
            {
              "@type": "CollectionPage",
              "@id": "https://drsullyspeaks.com/ebooks#collection",
              name: "My Books & Resources",
              description:
                "Complete library of Dr. Sully's workbooks, guides, and resources.",
              url: "https://drsullyspeaks.com/ebooks",
              inLanguage: "en",
              isPartOf: { "@id": "https://drsullyspeaks.com/#website" },
              publisher: publisherRef,
              author: AUTHOR,
              mainEntity: {
                "@type": "ItemList",
                "@id": "https://drsullyspeaks.com/ebooks#catalog",
                name: "Information products by Dr. Sully",
                numberOfItems: PRODUCTS.length,
                itemListOrder: "https://schema.org/ItemListOrderAscending",
                itemListElement: PRODUCTS.map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `https://drsullyspeaks.com/ebooks#${p.slug}`,
                  // Reference only — the full Book/Course/Service node lives at
                  // the top level of the @graph so each product is a first-class
                  // entity, not buried inside a list item.
                  item: { "@id": `https://drsullyspeaks.com/ebooks#${p.slug}` },
                })),
              },
            },
            ...PRODUCTS.map(productNode),

            {
              "@type": "BreadcrumbList",
              "@id": "https://drsullyspeaks.com/ebooks#breadcrumbs",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://drsullyspeaks.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Books & Resources",
                  item: "https://drsullyspeaks.com/ebooks",
                },
              ],
            },
            {
              "@type": "FAQPage",
              "@id": "https://drsullyspeaks.com/ebooks#faq",
              // Tie the FAQPage to the WebPage so Google credits the answers to
              // this URL (not to the ItemList) — the current recommended pattern
              // for pages that mix content types.
              mainEntityOfPage: { "@id": "https://drsullyspeaks.com/ebooks#webpage" },
              mainEntity: FAQS.map((f, i) => ({
                "@type": "Question",
                "@id": `https://drsullyspeaks.com/ebooks#faq-${i + 1}`,
                name: f.q,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: f.a,
                  author: AUTHOR,
                  inLanguage: "en",
                },
              })),
            },
          ],
        }}
        ogType="book"
        image="/og/ebooks.jpg"
        imageAlt="Dr. Sully's book & resource library — instant digital access to workbooks, guides, and playbooks."
      />
      <SiteNav />

      <main id="main">
        <section className="pt-32 md:pt-40 pb-16 md:pb-20">
          <div className="container max-w-5xl text-center">
            <Reveal variant="up">
              <span className="eyebrow !mx-auto">
                <LibraryBig className="inline h-3 w-3 mr-2" />
                My Books &amp; Resources
              </span>
              <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
                Every Book I've Written —{" "}
                <span className="text-orange">Instant Digital Access</span>
              </h1>
              <p className="text-silver text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                Complete library of my workbooks, guides, and resources. Download
                instantly.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 md:pb-28">
          <div className="container max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {PRODUCTS.map((p, i) => (
                <Reveal key={p.slug} variant="up" delay={i * 60}>
                  <article
                    id={p.slug}
                    className="h-full flex flex-col rounded-lg border border-border bg-navy p-6 md:p-7 hover:border-orange/60 transition-colors scroll-mt-32"
                  >
                    <h2 className="font-display font-bold uppercase text-white text-xl md:text-2xl leading-tight">
                      {p.title}
                    </h2>
                    <p className="text-orange text-xs md:text-sm uppercase tracking-[0.14em] mt-2">
                      {p.subtitle}
                    </p>
                    <p className="text-silver text-sm mt-4 leading-relaxed flex-1">
                      {p.desc}
                    </p>
                    <a
                      href={p.href}
                      {...(p.destination === "internal"
                        ? {}
                        : { target: "_blank", rel: "noopener noreferrer" })}
                      onClick={() =>
                        trackEvent("ebook_cta_click", {
                          label: "get_instant_access",
                          product_slug: p.slug,
                          product_title: p.title,
                          product_subtitle: p.subtitle,
                          product_url: p.href,
                          destination: p.destination,
                          price: p.price ?? 0,
                          currency: "USD",
                          position: i + 1,
                          path: "/ebooks",
                        })
                      }
                      className="mt-6 inline-flex items-center justify-center gap-2 self-start bg-orange text-white font-display uppercase tracking-[0.2em] text-xs px-5 py-3 rounded-sm hover:brightness-110 active:brightness-95 transition-all"
                      style={{ backgroundColor: "#E8710A" }}
                    >
                      {p.price ? `Get It — $${p.price}` : "Get Instant Access"}
                      {p.destination === "internal" ? (
                        <ArrowRight className="h-3.5 w-3.5" />
                      ) : (
                        <ExternalLink className="h-3.5 w-3.5" />
                      )}
                    </a>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-16 md:pb-20">
          <div className="container max-w-3xl">
            <Reveal variant="up">
              <h2 className="font-display uppercase tracking-[0.2em] text-orange text-xs mb-3 text-center">
                Frequently Asked
              </h2>
              <h3 className="font-display text-3xl md:text-4xl text-white text-center mb-10">
                Everything You Need to Know
              </h3>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="access" className="border-white/10">
                  <AccordionTrigger className="text-white font-display text-left hover:no-underline hover:text-orange">
                    How do I get access to a resource after I buy it?
                  </AccordionTrigger>
                  <AccordionContent className="text-silver leading-relaxed">
                    Every purchase is delivered instantly through Stan Store. As soon as
                    checkout completes you'll see a download link on the confirmation page
                    and receive an email with the same link. You can return to that link
                    any time — access does not expire.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="files" className="border-white/10">
                  <AccordionTrigger className="text-white font-display text-left hover:no-underline hover:text-orange">
                    What file types are the books and workbooks?
                  </AccordionTrigger>
                  <AccordionContent className="text-silver leading-relaxed">
                    Books, guides, and playbooks are delivered as PDFs that read cleanly
                    on phone, tablet, or desktop. Workbooks are fillable PDFs — you can
                    type directly into them or print and write by hand. No special
                    software required beyond a modern browser or any free PDF reader.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="audience" className="border-white/10">
                  <AccordionTrigger className="text-white font-display text-left hover:no-underline hover:text-orange">
                    Who are these resources for?
                  </AccordionTrigger>
                  <AccordionContent className="text-silver leading-relaxed">
                    Faith leaders, church staff, ministry founders, and Christian
                    professionals navigating purpose, church hurt, burnout, or building
                    something on Kingdom foundations. If you lead people, serve inside a
                    church, or are rebuilding after a hard season, these were written
                    with you in mind.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="refunds" className="border-white/10">
                  <AccordionTrigger className="text-white font-display text-left hover:no-underline hover:text-orange">
                    Can I share or re-download a book later?
                  </AccordionTrigger>
                  <AccordionContent className="text-silver leading-relaxed">
                    Your download link stays active — bookmark the confirmation email so
                    you can re-download on a new device. Resources are licensed for your
                    personal use; please don't redistribute the files. For team or
                    church-wide licensing, reach out on the Book a Session page.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </section>


        <section className="pb-24 md:pb-28">
          <div className="container max-w-3xl text-center">
            <Reveal variant="up">
              <p className="text-silver text-base md:text-lg">
                Not sure where to start?{" "}
                <Link
                  to="/assessment"
                  className="text-orange underline underline-offset-4 hover:brightness-110"
                >
                  Take the Assessment
                </Link>{" "}
                or{" "}
                <Link
                  to="/book"
                  className="text-orange underline underline-offset-4 hover:brightness-110"
                >
                  Book a Session
                </Link>
                .
              </p>
              <div className="mt-8 flex items-center justify-center gap-4">
                <Link
                  to="/assessment"
                  className="inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-6 py-4 rounded-sm text-white"
                  style={{ backgroundColor: "#E8710A" }}
                >
                  Take the Assessment
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs px-6 py-4 rounded-sm border border-orange text-white hover:bg-orange/10 transition-colors"
                >
                  Book a Session
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Cross-link to /books — sibling money page for the print-first reader. */}
        <section aria-label="Also explore" className="pb-24 md:pb-28 border-t border-white/10">
          <div className="container max-w-3xl text-center pt-16 md:pt-20">
            <p className="text-orange text-xs uppercase tracking-[0.3em] mb-3">
              Want a physical copy?
            </p>
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl leading-tight">
              Paperback &amp; hardcover editions —{" "}
              <Link to="/books" className="text-orange underline underline-offset-4 hover:brightness-110">
                shop the full bookshelf →
              </Link>
            </h2>
            <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
              Same titles in print. Great for gifts, teams, and study groups.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
